import React from 'react';
import { put, call, select, cancelled } from 'redux-saga/effects';
import { navigate } from '@/domain/router/redux/reduxActions';
import Api, { doLogout } from '@/domain/api';
import ROUTES_PATH from '@/router/routesPathConfig';
import { generatePath } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { yieldSuccessNotification, yieldValidationNotification, yieldErrorNotification } from '@/lib/toasts';
import * as action from '@/domain/companies/companiesActions';
import * as selector from '@/domain/companies/companiesSelector';
import tokenKeeper from '@/lib/apiTokenKeeper/apiTokenKeeper';
import { isDokkaSupportSelector, userFeaturesSelector, userIdSelector } from '@/domain/env/envSelector';
import { currentOrganizationSelector } from '@/domain/organization/organizationLeafSelectors';
import { sessionST } from '@/lib/sessionStorage';

import {
  companiesAdapter,
  configurationCompanyFactory,
  signerFactory,
  signingsFactory,
  createCompanyAdapter,
  signParamsFactory,
  SEARCH_TYPES,
} from '@/domain/companies/helpers';
import { queryToSearch } from '@/domain/router/helpers';
import { currentCompanyIdSelector } from '@/domain/router/routerLeafSelectors';
import { mergeSearchParams } from '@/domain/router/searchParams';
import * as ACL from '@/domain/restriction';
import { ensureGetCategories } from '@/domain/categories/sagas';
import { categoryAllSelector, categoryInboxSelector } from '@/domain/categories/selectors';
import { storage } from '@/lib/storage';
import { pipe, Option } from 'effect';

export { action, selector };

const isEmptyCompany = (company) => (company ? !(company.get('id') && company.get('checksum')) : true);

const getIsAllCompaniesLoaded = () => {
  try {
    return JSON.parse(sessionST().isAllCompaniesLoaded().get() || 'false');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Failed to read isAllCompaniesLoaded from LS');
    return false;
  }
};

export function* ensureGetCompanies(companyId = null) {
  const companyList = yield select(selector.companiesByIdSelector);

  if (!companyList.size) {
    sessionST().isAllCompaniesLoaded().set(JSON.stringify(false));
  }

  const isAllCompaniesLoaded = getIsAllCompaniesLoaded();
  const params = { unprocessed: true };
  const orgId = yield select(currentOrganizationSelector);

  // if we don't have companies for the current organization we need to load them
  // if we don't have orgId we should get all companies if we didn't do it before
  const isLoadedForCurrentOrg = orgId
    ? !!companyList.find(({ organizationId }) => organizationId === orgId)
    : isAllCompaniesLoaded;

  if (!companyList.size || isEmptyCompany(companyList.first()) || companyId || !isLoadedForCurrentOrg) {
    if (!companyId) {
      yield put({
        type: action.fetchCompanies.request,
      });
    }

    try {
      const { data } = yield call(Api.getCompanies, { params: { ...params, company_id: companyId, org_id: orgId } });

      const dokkaToken = yield call(tokenKeeper.getToken);
      // if we signed out we shouldn't update the state
      // handle this logic with cognito and no token selector and token in store
      if (dokkaToken) {
        yield put({
          ...params,
          type: action.fetchCompanies.success,
          payload: companiesAdapter(data),
        });
      }
      if (!orgId) {
        sessionST().isAllCompaniesLoaded().set(JSON.stringify(true));
      }
    } catch (err) {
      yield doLogout(action.fetchCompanies, err);
    } finally {
      if (yield cancelled()) {
        yield put({
          type: action.fetchCompanies.failure,
          err: 'cancelled',
        });
      }
    }
  }
}

export function* ensureGetCompany(id) {
  try {
    const { data } = yield call(Api.getCompany, { id });
    const signings = yield select(selector.companySigningsSelector);

    yield put({
      type: action.fetchCompany.success,
      payload: configurationCompanyFactory({ ...data, signings: signings.toJS() }),
    });
  } catch (err) {
    yield doLogout(action.fetchCompany, err);
  }
}

export function* ensureGetAllOrganizationCompanies({ resolve, reject, params }) {
  try {
    const { data } = yield call(Api.getAllCompanies, { params });
    if (typeof resolve === 'function') resolve(data);
  } catch (err) {
    if (typeof reject === 'function') reject();
    yield doLogout(action.getAllOrganizationCompanies, err);
  }
}

export function* ensureCompanySignIn() {
  yield put({ type: action.companySignInAction.request });
  try {
    // this API call will trigger company indexing on back-end
    yield call(Api.companyLogin, {});

    yield put({
      type: action.companySignInAction.success,
      payload: { companyId: tokenKeeper.getCompany() },
    });
  } catch (err) {
    yield doLogout(action.companySignInAction, err);
  }
}

export function* ensureCreateCompany({ formData, resolve, reject }) {
  try {
    const { data } = yield call(Api.createCompany, {
      params: {},
      data: formData,
    });
    yield put({
      type: action.createCompanyAction.success,
      payload: createCompanyAdapter(data),
    });
    yield put({
      type: action.fetchCompany.success,
      payload: configurationCompanyFactory(data),
    });
    yield put(navigate.push(generatePath(ROUTES_PATH.COMPANY_SETTINGS_GENERAL_MAIN.absolute, { companyId: data.id })));
    if (typeof resolve === 'function') resolve();
  } catch (err) {
    yieldValidationNotification(err);
    if (typeof reject === 'function') reject(err);
    yield doLogout(action.createCompanyAction, err);
  }
}

export function* ensureGetCompanyPreferences(companyID) {
  try {
    const { data } = yield call(Api.getSettings, {
      params: {
        category: 'internal',
        key: 'SIGNING',
        companyID,
      },
    });
    yield put({
      type: action.updateSignParams.success,
      payload: signParamsFactory(data),
      params: {
        id: companyID,
      },
    });
  } catch (err) {
    yield put({
      type: action.updateSignParams.failure,
      params: {
        id: companyID,
      },
      err,
    });
  }
}

export function* ensureMarkCompanyPinned({ params }) {
  const companies = yield select(selector.companiesByIdSelector);
  const pinned = companies.getIn([params.id, 'pinned']) ? 'n' : 'y';
  try {
    yield call(Api.updateFavoriteCompanies, {
      data: {
        companyID: params.id,
        pinned,
      },
    });
    yield put({
      type: action.markCompanyPinned.success,
      payload: {
        pinned: pinned === 'y',
      },
      params,
    });
  } catch (err) {
    yield put({
      type: action.markCompanyPinned.failure,
      params,
    });
  }
}

export function* ensureUploadLogo({ file, resolve, reject }) {
  const id = yield select(selector.configurationCompanyIdSelector);
  try {
    const formData = new global.FormData();
    formData.append('picture', file);
    const { data } = yield call(Api.updateCompanyLogo, {
      data: formData,
      id,
    });
    yield put({
      type: action.updateLogoCompany.success,
      payload: {
        id,
        picture: data.picture,
      },
    });
    if (typeof resolve === 'function') resolve(data.picture);
  } catch (err) {
    if (typeof reject === 'function') reject(err);
  }
}

export function* ensureUpdateCompany(formData) {
  const id = yield select(selector.configurationCompanyIdSelector);
  const signings = yield select(selector.companySigningsSelector);

  try {
    const { data } = yield call(Api.updateCompany, {
      data: formData,
      id,
    });
    yield put({
      type: action.updateCompanyAction.success,
      payload: configurationCompanyFactory({ ...data, signings: signings.toJS() }),
    });
    yield put({
      type: action.updateCompanyEmailBox.type,
      id,
      payload: {
        email: data.email_box,
        confidential: data.confidential_email_box,
      },
    });
    yield put({
      type: action.updateCompanyInList.success,
      id,
      payload: data,
    });
    yieldSuccessNotification(
      <FormattedMessage id="forms.organization.toast.messages.company_updated" defaultMessage="Company updated" />,
    );
  } catch (err) {
    yieldValidationNotification(err);
    yield doLogout(action.updateCompanyAction, err);
  }
}

export function* ensureDeleteCompany() {
  const id = yield select(selector.configurationCompanyIdSelector);
  try {
    yield call(Api.deleteCompany, {
      id,
    });
    yieldSuccessNotification(
      <FormattedMessage id="forms.company.toast.messages.company_deleted" defaultMessage="Company Deleted" />,
    );
    yield put({
      type: action.deleteCompany.success,
      payload: id,
    });
    yield put(navigate.push(generatePath(ROUTES_PATH.COMPANIES.absolute)));
  } catch (err) {
    yieldErrorNotification(
      <FormattedMessage
        id="app.selector.inquiryError"
        defaultMessage="We encountered temporary error sending your inquiry"
      />,
    );
  }
}

export function* ensureGetCompanySignings(cid) {
  const companyId = cid || (yield select(currentCompanyIdSelector));
  try {
    const { data } = yield call(Api.getSignings, {
      id: companyId,
      type: 'company',
    });

    yield put({
      type: action.getCompanySignings.success,
      payload: signingsFactory(data),
      companyId,
    });
  } catch (err) {
    yield doLogout(action.getCompanySignings, err);
  }
}

export function* ensureUpdateCompanySignings(formData) {
  const companyId = yield select(selector.configurationCompanyIdSelector);
  try {
    const { data } = yield call(Api.updateSignings, {
      data: formData,
      id: companyId,
      type: 'company',
    });
    yield put({
      type: action.updateCompanySigningsAction.success,
      payload: signerFactory(data),
    });
    yieldSuccessNotification(
      <FormattedMessage
        id="forms.company.toast.messages.company_signings_updated"
        defaultMessage="Signing configuration updated"
      />,
    );
  } catch (err) {
    yieldValidationNotification(err);
    yield doLogout(action.updateCompanySigningsAction, err);
  }
}

export function* ensureRemoveCompanySigner({ tokenName, idx }) {
  const companyId = yield select(selector.configurationCompanyIdSelector);
  try {
    if (tokenName) {
      yield call(Api.removeSignings, {
        data: {
          token_name: tokenName,
        },
        type: 'company',
        id: companyId,
      });
    }
    yield put({
      type: action.removeCompanySignerAction.success,
      payload: {
        tokenName,
        idx,
      },
    });
    yieldSuccessNotification(
      <FormattedMessage
        id="forms.company.toast.messages.company_signings_updated"
        defaultMessage="Signing configuration updated"
      />,
    );
  } catch (err) {
    yieldValidationNotification(err);
    yield doLogout(action.removeCompanySignerAction, err);
  }
}

export function* ensureSetCompanySigningsGuid(formData) {
  const companyId = yield select(selector.configurationCompanyIdSelector);
  try {
    const { data } = yield call(Api.setGuidSignings, {
      data: {
        guid: formData.guid,
      },
      type: 'company',
      id: companyId,
    });
    yield put({
      type: action.setCompanySigningsGuidAction.success,
      payload: data.guid,
    });
    yieldSuccessNotification(
      <FormattedMessage
        id="forms.company.toast.messages.company_signings_updated"
        defaultMessage="Signing configuration updated"
      />,
    );
  } catch (err) {
    yieldValidationNotification(err);
    yield doLogout(action.setCompanySigningsGuidAction, err);
  }
}

export function* ensureGetCompanySigningsCopy() {
  const companyId = yield select(selector.configurationCompanyIdSelector);
  try {
    const { data } = yield call(Api.getSigningsCopy, {
      id: companyId,
    });
    yield put({
      type: action.getCompanySignings.success,
      payload: signingsFactory(data),
      companyId,
    });
    yieldSuccessNotification(
      <FormattedMessage
        id="forms.company.toast.messages.company_signings_updated"
        defaultMessage="Signing configuration updated"
      />,
    );
  } catch (err) {
    yieldValidationNotification(err);
    yield doLogout(action.setCompanySigningsGuidAction, err);
  }
}

export function* ensureRemoveAllCompanySignings() {
  const id = yield select(selector.configurationCompanyIdSelector);
  try {
    yield call(Api.deleteAllSignings, {
      type: 'company',
      id,
    });
    yield put({
      type: action.removeAllCompanySignerAction.success,
    });
    yieldSuccessNotification(
      <FormattedMessage
        id="forms.company.toast.messages.company_signings_removed"
        defaultMessage="Signing configuration removed"
      />,
    );
  } catch (err) {
    yield doLogout(action.removeAllCompanySignerAction, err);
  }
}

export function* ensureBackToList() {
  const isGhost = yield select(isDokkaSupportSelector);
  yield put(navigate.push(isGhost ? generatePath(ROUTES_PATH.ORGANIZATIONS.absolute) : '/'));
}

export function* ensureGetSlackConnection(companyId) {
  try {
    const { data } = yield call(Api.getSlackConnection, {
      id: companyId,
    });

    yield put({
      type: action.getCompanySlackConnectionAction.success,
      payload: data,
    });
  } catch (err) {
    yield doLogout(action.getCompanySlackConnectionAction, err);
  }
}

export function* ensureConnectSlack() {
  try {
    const { data } = yield call(Api.connectSlack);

    window.location = data.endpoint;
  } catch (err) {
    yield doLogout(action.connectSlackAction, err);
  }
}

export function* ensureDisconnectSlack(companyId) {
  try {
    yield call(Api.disconnectSlack);
    yield call(ensureGetSlackConnection, companyId);

    yieldSuccessNotification(
      <FormattedMessage
        id="configurations.applications.toast.disconnected.success"
        defaultMessage="Application disconnected successfully"
      />,
    );
  } catch (err) {
    yield doLogout(action.disconnectSlackAction, err);
  }
}

export function* ensureSearch(value) {
  try {
    const searchType = yield select(selector.searchTypeSelector);
    const res = {
      filter: '',
      text: '',
    };

    switch (searchType) {
      case SEARCH_TYPES.byDocumentText:
        if (value.length > 2) {
          yield put({
            type: action.setSearchFilterAction.request,
          });
          const { data } = yield call(Api.searchInCompanies, {
            params: {
              stag: [value],
            },
            paramsSerializer: (d) => queryToSearch(d),
          });
          res.filter = data;
          res.text = value;
        } else {
          res.text = value;
        }
        break;
      case SEARCH_TYPES.byCompany:
      default:
        res.filter = value;
        res.text = value;
    }

    yield put({
      type: action.setSearchFilterAction.success,
      payload: res,
    });
  } catch (err) {
    yield doLogout(action.setSearchFilterAction, err);
  }
}

export function* ensureChangeSearchType() {
  try {
    const value = yield select(selector.searchTextSelector);
    yield call(ensureSearch, value);
  } catch (err) {
    yield doLogout(action.setSearchTypeAction, err);
  }
}

export function* ensureRedirectToDefaultCategory(params, routePath) {
  const advancedParams = {
    companyType: params.companyType,
  };

  const isGranted = yield select(ACL.isGranted);
  yield call(ensureGetCategories, advancedParams);

  const { filter_documents_tagged_by_user_only } = yield select(userFeaturesSelector);
  const preferredCategory = yield select(isGranted(ACL.IS_ACCOUNT) ? categoryInboxSelector : categoryAllSelector);
  // Categories are served for the role the server holds. A session that missed a role change can
  // still name a default the server no longer returns; without this fallback entering the company hangs.
  const allCategory = yield select(categoryAllSelector);
  const category = pipe(
    Option.fromNullable(preferredCategory),
    Option.orElse(() => Option.fromNullable(allCategory)),
    Option.getOrNull,
  );
  const userTag = yield select(userIdSelector);

  if (category) {
    // This redirect resolves a category; it is not a filter on the query. Every parameter an inbound
    // link carries is the reason that link was followed, so the whole query rides through — any
    // surface can deep-link into the workspace without this saga being taught about it first.
    const mergedSearch = mergeSearchParams(
      window.location.search,
      filter_documents_tagged_by_user_only ? [['text[]', `#${userTag}`]] : [],
    );
    const queryParams = mergedSearch ? `?${mergedSearch}` : '';

    const to = generatePath(routePath, {
      companyId: params.companyId,
      category1: category.id,

      ...(queryParams && { queryParams }),
    });

    // Replace, not push: the uncategorised URL must not stay in history, or Back lands on it and redirects again.
    yield put(navigate.replace(to));
  }
}

export function* ensureUpdateCompaniesWorkSpaceType({ payload }) {
  try {
    const companiesWorkSpaceStr = yield storage().companiesWorkSpaceType().get();
    const companiesWorkSpace = companiesWorkSpaceStr ? JSON.parse(companiesWorkSpaceStr) : {};
    companiesWorkSpace[payload.orgId] = payload.type;
    yield storage().companiesWorkSpaceType().set(JSON.stringify(companiesWorkSpace));
  } catch (err) {
    console.log('error when save workspace type in localstore');
  }
}
